import React, { useState, useRef } from 'react';
import { Mic, Square, Send, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * Enregistreur de message vocal. Demande l'accès au micro au clic, enregistre via
 * MediaRecorder, puis permet d'écouter avant envoi ou d'annuler.
 */
const VoiceRecorder = ({ onSend, disabled }) => {
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [error, setError] = useState('');
  const [duration, setDuration] = useState(0);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  const startRecording = async () => {
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType || 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setRecording(true);
      setDuration(0);
      timerRef.current = setInterval(() => setDuration((d) => d + 1), 1000);
    } catch (e) {
      setError("Impossible d'accéder au microphone. Vérifiez les autorisations de votre navigateur.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const cancelRecording = () => {
    setAudioBlob(null);
    setAudioUrl(null);
    setDuration(0);
  };

  const handleSend = async () => {
    if (audioBlob) {
      await onSend(audioBlob);
      cancelRecording();
    }
  };

  const formatDuration = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  if (error) {
    return <div className="text-xs text-destructive px-2" data-testid="voice-recorder-error">{error}</div>;
  }

  if (audioUrl) {
    return (
      <div className="flex items-center gap-2 bg-muted rounded-full px-3 py-1.5">
        <audio src={audioUrl} controls className="h-8 max-w-[160px]" />
        <button onClick={cancelRecording} className="text-muted-foreground hover:text-destructive" title="Annuler" data-testid="voice-cancel-btn">
          <Trash2 className="h-4 w-4" />
        </button>
        <button onClick={handleSend} className="text-primary hover:text-primary/80" title="Envoyer" data-testid="voice-send-btn">
          <Send className="h-4 w-4" />
        </button>
      </div>
    );
  }

  if (recording) {
    return (
      <div className="flex items-center gap-2 bg-destructive/10 rounded-full px-3 py-1.5">
        <span className="w-2 h-2 rounded-full bg-destructive animate-pulse" />
        <span className="text-xs text-destructive font-medium">{formatDuration(duration)}</span>
        <button onClick={stopRecording} title="Arrêter" data-testid="voice-stop-btn">
          <Square className="h-4 w-4 text-destructive" />
        </button>
      </div>
    );
  }

  return (
    <Button type="button" variant="ghost" size="icon" onClick={startRecording} disabled={disabled} title="Message vocal" data-testid="voice-record-btn">
      <Mic className="h-5 w-5" />
    </Button>
  );
};

export default VoiceRecorder;
